import axios from 'axios';
import { API_BASE_URL } from '../config/apiBaseUrl';

const DEFAULT_GET_CACHE_TTL_MS = 90 * 1000;
const GET_CACHE_STORAGE_KEY = 'receipt-tracker:get-cache:v1';
const MAX_PERSISTED_CACHE_ENTRIES = 120;

const responseCache = new Map();
const inflightGetRequests = new Map();
let cacheGeneration = 0;
let activeAccountId = null;
const accountContextInvalidationListeners = new Set();

const isAccountScopedUrl = (url = '') =>
  ['/api/categories', '/api/transactions', '/api/budgets', '/api/dashboard', '/api/shopping-lists']
    .some((prefix) => String(url).startsWith(prefix));

const canUseLocalStorage = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return Boolean(window.localStorage);
  } catch (error) {
    return false;
  }
};

const persistCacheState = () => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    const now = Date.now();
    const validEntries = [];

    responseCache.forEach((entry, key) => {
      if (entry && Number(entry.expiresAt) > now) {
        validEntries.push([key, entry]);
      }
    });

    const trimmedEntries = validEntries.slice(-MAX_PERSISTED_CACHE_ENTRIES);
    const payload = {
      generation: cacheGeneration,
      entries: trimmedEntries,
    };

    window.localStorage.setItem(GET_CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // Ignore storage serialization/quota errors and continue with in-memory cache.
  }
};

const hydrateCacheState = () => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    const rawValue = window.localStorage.getItem(GET_CACHE_STORAGE_KEY);
    if (!rawValue) {
      return;
    }

    const parsed = JSON.parse(rawValue);
    const persistedGeneration = Number(parsed?.generation);
    cacheGeneration = Number.isFinite(persistedGeneration) ? persistedGeneration : 0;

    const persistedEntries = Array.isArray(parsed?.entries) ? parsed.entries : [];
    const now = Date.now();
    persistedEntries.forEach((entryTuple) => {
      if (!Array.isArray(entryTuple) || entryTuple.length !== 2) {
        return;
      }
      const [key, entry] = entryTuple;
      if (typeof key !== 'string' || !entry || Number(entry.expiresAt) <= now) {
        return;
      }
      responseCache.set(key, entry);
    });
  } catch (error) {
    // Ignore malformed storage payloads and continue with empty cache.
  }
};

const normalizeForCacheKey = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeForCacheKey);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeForCacheKey(value[key]);
        return accumulator;
      }, {});
  }
  return value;
};

const getAuthCacheScope = () => {
  const token = localStorage.getItem('token');
  return `${token ? token.slice(-16) : 'anonymous'}::${activeAccountId || 'self'}`;
};

const buildGetCacheKey = (url, params) => {
  const normalizedParams = normalizeForCacheKey(params || {});
  return [
    String(cacheGeneration),
    getAuthCacheScope(),
    url,
    JSON.stringify(normalizedParams),
  ].join('::');
};

const invalidateGetCache = () => {
  cacheGeneration += 1;
  responseCache.clear();
  inflightGetRequests.clear();
  persistCacheState();
};

export const setActiveAccountContext = (accountId) => {
  activeAccountId = accountId || null;
  invalidateGetCache();
};

export const getActiveAccountContext = () => activeAccountId;

export const subscribeToAccountContextInvalidation = (listener) => {
  accountContextInvalidationListeners.add(listener);
  return () => accountContextInvalidationListeners.delete(listener);
};

const getCachedResponseData = (url, options = {}) => {
  const {
    params,
    forceRefresh = false,
    ttlMs = DEFAULT_GET_CACHE_TTL_MS,
    staleWhileRevalidate = false,
    onBackgroundUpdate,
  } = options;

  const key = buildGetCacheKey(url, params);
  const now = Date.now();

  const runNetworkFetch = () => {
    const requestPromise = api.get(url, { params })
      .then((response) => {
        const data = response.data;
        responseCache.set(key, {
          data,
          expiresAt: Date.now() + ttlMs,
        });
        persistCacheState();
        return data;
      })
      .finally(() => {
        inflightGetRequests.delete(key);
      });

    inflightGetRequests.set(key, requestPromise);
    return requestPromise;
  };

  if (!forceRefresh) {
    const cachedEntry = responseCache.get(key);
    if (cachedEntry && cachedEntry.expiresAt > now) {
      // Touch item to keep recently used entries longer in persisted cache.
      responseCache.delete(key);
      responseCache.set(key, cachedEntry);

      if (staleWhileRevalidate) {
        const inflightRequest = inflightGetRequests.get(key) || runNetworkFetch();
        if (typeof onBackgroundUpdate === 'function') {
          inflightRequest
            .then((freshData) => onBackgroundUpdate(freshData))
            .catch(() => {
              // Ignore refresh errors and keep serving existing cache.
            });
        }
      }
      return Promise.resolve(cachedEntry.data);
    }

    const inflightRequest = inflightGetRequests.get(key);
    if (inflightRequest) {
      return inflightRequest;
    }
  }

  const requestPromise = runNetworkFetch();
  return requestPromise;
};

hydrateCacheState();

// Configure axios
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (activeAccountId && isAccountScopedUrl(config.url)) {
    config.headers['X-Account-Context'] = activeAccountId;
  } else {
    delete config.headers['X-Account-Context'];
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const hadAccountContext = Boolean(error?.config?.headers?.['X-Account-Context']);
    if (
      hadAccountContext
      && error?.response?.status === 403
      && error?.response?.data?.code === 'ACCOUNT_CONTEXT_FORBIDDEN'
    ) {
      setActiveAccountContext(null);
      accountContextInvalidationListeners.forEach((listener) => listener(error));
      error.isAccountContextInvalidation = true;
    }
    return Promise.reject(error);
  }
);

// Categories API
export const categoriesAPI = {
  // Get all categories
  getAll: async (options = {}) => {
    const data = await getCachedResponseData('/api/categories', {
      ttlMs: 5 * 60 * 1000,
      ...options,
    });
    return data.categories;
  },

  // Create category
  create: async (categoryData) => {
    const response = await api.post('/api/categories', categoryData);
    invalidateGetCache();
    return response.data.category;
  },

  // Update category
  update: async (id, categoryData) => {
    const response = await api.put(`/api/categories/${id}`, categoryData);
    invalidateGetCache();
    return response.data.category;
  },

  // Delete category
  delete: async (id) => {
    await api.delete(`/api/categories/${id}`);
    invalidateGetCache();
  },
};

// Transactions API
export const transactionsAPI = {
  // Get all transactions
  getAll: async (paramsOrOptions = {}, maybeOptions = {}) => {
    const optionKeys = new Set(['forceRefresh', 'ttlMs', 'staleWhileRevalidate', 'onBackgroundUpdate']);
    const firstArg = paramsOrOptions && typeof paramsOrOptions === 'object' ? paramsOrOptions : {};
    const secondArg = maybeOptions && typeof maybeOptions === 'object' ? maybeOptions : {};
    const treatFirstArgAsOptions = Object.keys(secondArg).length === 0
      && Object.keys(firstArg).some((key) => optionKeys.has(key));
    const params = treatFirstArgAsOptions ? undefined : firstArg;
    const requestOptions = treatFirstArgAsOptions ? firstArg : secondArg;

    const data = await getCachedResponseData('/api/transactions', {
      params,
      ttlMs: 2 * 60 * 1000,
      ...requestOptions,
    });
    return data.transactions;
  },

  // Get projected upcoming recurring transactions
  getUpcomingRecurring: async (options = {}) => {
    const { onBackgroundUpdate, ...restOptions } = options;
    const data = await getCachedResponseData('/api/transactions/recurring/upcoming', {
      ttlMs: 60 * 1000,
      ...restOptions,
      onBackgroundUpdate: typeof onBackgroundUpdate === 'function'
        ? (freshResponseData) => onBackgroundUpdate(freshResponseData?.upcomingRecurring || [])
        : undefined,
    });
    return data.upcomingRecurring || [];
  },
  // Get recurring transaction templates
  getRecurringTemplates: async (options = {}) => {
    const data = await getCachedResponseData('/api/transactions/recurring/templates', {
      ttlMs: 60 * 1000,
      ...options,
    });
    return data.recurringTemplates || [];
  },
  // Update recurring transaction template
  updateRecurringTemplate: async (id, payload) => {
    const response = await api.patch(`/api/transactions/recurring/templates/${id}`, payload);
    invalidateGetCache();
    return response.data.recurringTemplate;
  },
  // Deactivate recurring transaction template
  deleteRecurringTemplate: async (id) => {
    const response = await api.delete(`/api/transactions/recurring/templates/${id}`);
    invalidateGetCache();
    return response.data;
  },
  // Deactivate entire recurring series based on a template seed
  deleteRecurringTemplateSeries: async (id) => {
    const response = await api.delete(`/api/transactions/recurring/templates/${id}/series`);
    invalidateGetCache();
    return response.data;
  },
  // Delete selected occurrence date and all future occurrences in the same recurring series
  deleteRecurringTemplateFuture: async (id, applyFromDate) => {
    const response = await api.delete(`/api/transactions/recurring/templates/${id}/future`, {
      data: { applyFromDate },
    });
    invalidateGetCache();
    return response.data;
  },
  // Record a projected recurring occurrence as a real transaction
  recordRecurringPayment: async (id, occurrenceDate) => {
    const response = await api.post(`/api/transactions/recurring/${id}/record-payment`, {
      occurrenceDate,
    });
    invalidateGetCache();
    return response.data;
  },

  // Get single transaction
  getById: async (id, options = {}) => {
    const data = await getCachedResponseData(`/api/transactions/${id}`, {
      ttlMs: 2 * 60 * 1000,
      ...options,
    });
    return data.transaction;
  },

  // Get transactions by date range
  getByDateRange: async (startDate, endDate, options = {}) => {
    const data = await getCachedResponseData('/api/transactions/date-range', {
      params: { startDate, endDate },
      ttlMs: 2 * 60 * 1000,
      ...options,
    });
    return data.transactions;
  },

  // Create transaction
  create: async (transactionData) => {
    const response = await api.post('/api/transactions', transactionData);
    invalidateGetCache();
    return response.data;
  },

  // Bulk import historical transactions from spreadsheet exports
  importHistorical: async (payload) => {
    const response = await api.post('/api/transactions/import-historical', payload);
    invalidateGetCache();
    return response.data;
  },

  // Upload receipt image
  uploadReceipt: async (file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    const response = await api.post('/api/transactions/upload-receipt', formData);
    invalidateGetCache();
    return response.data;
  },

  // Process receipt image via Document AI + Gemini category suggestion
  processReceipt: async (file) => {
    const formData = new FormData();
    formData.append('receipt', file);
    const response = await api.post('/api/transactions/process-receipt', formData);
    return response.data.receiptDraft;
  },

  // Commit reviewed receipt to transactions and return budget snapshot
  commitReceipt: async (reviewedReceipt) => {
    const response = await api.post('/api/transactions/commit-receipt', reviewedReceipt);
    invalidateGetCache();
    return response.data;
  },

  // Debug: inspect learned item-to-category mappings
  getLearnedCategoryMappings: async (limit = 300, options = {}) => {
    const data = await getCachedResponseData('/api/transactions/learned-category-mappings', {
      params: { limit },
      ttlMs: 60 * 1000,
      ...options,
    });
    return data.mappings;
  },

  getExperimentalDictionaryStats: async () => {
    const response = await api.get('/api/transactions/experimental/dictionary/stats');
    return response.data;
  },

  resolveExperimentalDictionaryItems: async (payload) => {
    const response = await api.post('/api/transactions/experimental/dictionary/resolve', payload);
    return response.data;
  },

  recordExperimentalAliasEdit: async (payload) => {
    const response = await api.post('/api/transactions/experimental/dictionary/alias-edit', payload);
    return response.data;
  },

  seedExperimentalDictionary: async (payload) => {
    const response = await api.post('/api/transactions/experimental/dictionary/seed', payload);
    return response.data;
  },

  bootstrapExperimentalDictionary: async (payload) => {
    const response = await api.post('/api/transactions/experimental/dictionary/bootstrap', payload);
    return response.data;
  },

  syncExperimentalDictionaryOpenFoodFacts: async (payload) => {
    const response = await api.post('/api/transactions/experimental/dictionary/sync/open-food-facts', payload);
    return response.data;
  },

  // Update transaction
  update: async (id, transactionData) => {
    try {
      const response = await api.put(`/api/transactions/${id}`, transactionData);
      invalidateGetCache();
      return response.data.transaction;
    } catch (putError) {
      const status = putError?.response?.status;
      const hasPayload = transactionData && Object.keys(transactionData).length > 0;
      const methodLikelyBlocked = !putError?.response || status === 404 || status === 405 || status === 501;
      const putMayBeBrokenInProd = hasPayload && status >= 500;
      if (!methodLikelyBlocked && !putMayBeBrokenInProd) {
        throw putError;
      }

      try {
        const fallbackResponse = await api.post(`/api/transactions/${id}/update`, transactionData);
        invalidateGetCache();
        return fallbackResponse.data.transaction;
      } catch (fallbackError) {
        const fallbackStatus = fallbackError?.response?.status;
        if (fallbackStatus === 404 || fallbackStatus === 405) {
          throw putError;
        }
        throw fallbackError;
      }
    }
  },

  // Delete a transaction
  delete: async (id) => {
    await api.delete(`/api/transactions/${id}`);
    invalidateGetCache();
  },
};

// Budgets API
export const budgetsAPI = {
  // Get budgets, optionally filtered by month/year
  getAll: async (month, year, options = {}) => {
    const data = await getCachedResponseData('/api/budgets', {
      params: month && year ? { month, year } : undefined,
      ttlMs: 90 * 1000,
      ...options,
    });
    return data.budgets;
  },

  // Create budget
  create: async (budgetData) => {
    const response = await api.post('/api/budgets', budgetData);
    invalidateGetCache();
    return response.data.budget;
  },

  // Update budget
  update: async (id, budgetData) => {
    const response = await api.put(`/api/budgets/${id}`, budgetData);
    invalidateGetCache();
    return response.data.budget;
  },

  // Preview or apply budgets from prior month spending
  applyFromSpending: async (payload) => {
    const response = await api.post('/api/budgets/apply-from-spending', payload);
    invalidateGetCache();
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  get: async (month, year, options = {}) => {
    const { onBackgroundUpdate, ...restOptions } = options;
    const data = await getCachedResponseData('/api/dashboard', {
      params: month && year ? { month, year } : undefined,
      ttlMs: 90 * 1000,
      ...restOptions,
      onBackgroundUpdate: typeof onBackgroundUpdate === 'function'
        ? (freshResponseData) => onBackgroundUpdate(freshResponseData?.dashboard || freshResponseData || {})
        : undefined,
    });
    return data?.dashboard || data || {};
  },
  ask: async ({
    question,
    month,
    year,
    conversation = [],
  }) => {
    const response = await api.post('/api/dashboard/ask', {
      question,
      month,
      year,
      conversation,
    });
    invalidateGetCache();
    return response.data || {};
  },
  askStream: async ({
    question,
    month,
    year,
    conversation = [],
    onEvent,
    signal,
  }) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/api/dashboard/ask-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        question,
        month,
        year,
        conversation,
      }),
      signal,
    });

    if (!response.ok) {
      let errorMessage = 'Failed to answer dashboard question';
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorMessage;
      } catch (error) {
        // Ignore malformed non-JSON errors.
      }
      throw new Error(errorMessage);
    }

    if (!response.body) {
      throw new Error('Streaming not available in this browser.');
    }

    const decoder = new TextDecoder();
    const reader = response.body.getReader();
    let buffer = '';
    let finalPayload = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = String(rawLine || '').trim();
        if (!line) {
          continue;
        }

        let event = null;
        try {
          event = JSON.parse(line);
        } catch (error) {
          event = null;
        }
        if (!event) {
          continue;
        }

        if (typeof onEvent === 'function') {
          onEvent(event);
        }
        if (event.type === 'final') {
          finalPayload = event.payload || null;
        }
        if (event.type === 'error') {
          throw new Error(String(event.error || 'Failed to answer dashboard question'));
        }
      }
    }

    invalidateGetCache();
    return finalPayload || {};
  },
};

// Shopping Lists API
export const shoppingListsAPI = {
  getAll: async (options = {}) => {
    const data = await getCachedResponseData('/api/shopping-lists', {
      ttlMs: 60 * 1000,
      ...options,
    });
    return data.shoppingLists;
  },
  getById: async (id, options = {}) => {
    const data = await getCachedResponseData(`/api/shopping-lists/${id}`, {
      ttlMs: 60 * 1000,
      ...options,
    });
    return data.shoppingList;
  },
  historyItems: async (id, params = {}, options = {}) => {
    const data = await getCachedResponseData(`/api/shopping-lists/${id}/history-items`, {
      params,
      ttlMs: 60 * 1000,
      ...options,
    });
    return data;
  },
  create: async (payload) => {
    const response = await api.post('/api/shopping-lists', payload);
    invalidateGetCache();
    return response.data.shoppingList;
  },
  generate: async (payload) => {
    const response = await api.post('/api/shopping-lists/generate', payload);
    invalidateGetCache();
    return response.data;
  },
  update: async (id, payload) => {
    const response = await api.put(`/api/shopping-lists/${id}`, payload);
    invalidateGetCache();
    return response.data.shoppingList;
  },
  delete: async (id) => {
    await api.delete(`/api/shopping-lists/${id}`);
    invalidateGetCache();
  },
  addItem: async (listId, payload) => {
    const response = await api.post(`/api/shopping-lists/${listId}/items`, payload);
    invalidateGetCache();
    return response.data.item;
  },
  addItemFromHistory: async (listId, payload) => {
    const response = await api.post(`/api/shopping-lists/${listId}/items/from-history`, payload);
    invalidateGetCache();
    return response.data;
  },
  reorderItems: async (listId, payload) => {
    const response = await api.post(`/api/shopping-lists/${listId}/items/reorder`, payload);
    invalidateGetCache();
    return response.data.shoppingList;
  },
  updateItem: async (listId, itemId, payload) => {
    const response = await api.put(`/api/shopping-lists/${listId}/items/${itemId}`, payload);
    invalidateGetCache();
    return response.data.item;
  },
  deleteItem: async (listId, itemId) => {
    await api.delete(`/api/shopping-lists/${listId}/items/${itemId}`);
    invalidateGetCache();
  },
  resetChecks: async (listId) => {
    const response = await api.post(`/api/shopping-lists/${listId}/reset-checks`);
    invalidateGetCache();
    return response.data.shoppingList;
  },
};

export const demoSignupsAPI = {
  create: async (payload) => {
    const response = await api.post('/api/demo-signups', payload);
    return response.data;
  },
  listAdmin: async (token) => {
    const response = await api.get('/api/demo-signups/admin', {
      params: { token },
    });
    return response.data;
  },
  getExportUrl: (token) => `${API_BASE_URL}/api/demo-signups/admin/export.csv?token=${encodeURIComponent(token)}`,
};

export const accountAPI = {
  previewInvite: async (token) => {
    const response = await api.get(`/api/account/invite/${encodeURIComponent(token)}`);
    return response.data;
  },
  acceptInvite: async (token) => {
    const response = await api.post(`/api/account/invite/${encodeURIComponent(token)}/accept`);
    invalidateGetCache();
    return response.data;
  },
  invite: async (email) => {
    const response = await api.post('/api/account/invite', { email });
    return response.data.membership;
  },
  getMembers: async () => {
    const response = await api.get('/api/account/members');
    return response.data.memberships;
  },
  revoke: async (membershipId) => {
    await api.delete(`/api/account/members/${membershipId}`);
  },
  leave: async (accountId) => {
    await api.post(`/api/account/leave/${accountId}`);
  },
  getActivity: async (accountId) => {
    const response = await api.get(`/api/account/${accountId}/activity`);
    return response.data.activities;
  },
};

export default api;
